import type { Delivery } from '@interfaces/ILogistics';
import type { CardTranslate } from '../panels/boardCard.logic';
import { addDays } from '../logistics.format';
import { DAYS_PER_WEEK, weekStart, type CalendarMode } from './calendar.logic';

const intlLocale = (lang: string) => (lang === 'ja' ? 'ja-JP' : 'en-US');
const utcDate = (day: string) => new Date(`${day}T00:00:00Z`);

/** The page title: "November 2026", or a week's "Nov 1 – 7". */
export function periodLabel(mode: CalendarMode, anchor: string, lang: string): string {
  const locale = intlLocale(lang);
  if (mode === 'month') {
    return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(utcDate(anchor));
  }
  const first = weekStart(anchor);
  const format = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' });
  return format.formatRange(utcDate(first), utcDate(addDays(first, DAYS_PER_WEEK - 1)));
}

/** The grid's header: seven short weekday names, Sunday first. */
export function weekdayLabels(lang: string): string[] {
  const format = new Intl.DateTimeFormat(intlLocale(lang), { weekday: 'short', timeZone: 'UTC' });
  const sunday = weekStart('2026-01-01');
  return Array.from({ length: DAYS_PER_WEEK }, (_, offset) => format.format(utcDate(addDays(sunday, offset))));
}

/** A day spelled for reading: "Sun, Nov 1". */
export function dayLabel(day: string, lang: string): string {
  return new Intl.DateTimeFormat(intlLocale(lang), { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(utcDate(day));
}

/** What a small surface calls an order's cargo: its first material, plus how
 *  many more ride with it. An order always has a line (the backend refuses
 *  one without). */
export function cargoLabel(delivery: Delivery, t: CardTranslate): string {
  const [first, ...rest] = delivery.lines;
  return rest.length > 0
    ? t('logistics.calendar.moreMaterials', { first: first!.itemName, n: rest.length })
    : first!.itemName;
}
